import { Injectable } from '@angular/core';
import { ScoreResponse, Hole } from '../../../models';

export interface PlusMinusCalculationResult {
  updatedScores: ScoreResponse[];
  finalCumulativePlusMinus: number;
}

export interface ScorecardCalculationInput {
  scores: ScoreResponse[];
  holes: Hole[];
  startPlusMinus?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ScorecardCalculationService {

  /**
   * Calculates cumulative plus/minus for a set of scores
   * Pure function - does not modify input arrays
   */
  calculateCumulativePlusMinus(input: ScorecardCalculationInput): PlusMinusCalculationResult {
    const { scores, holes, startPlusMinus = 0 } = input;

    // Create a copy to avoid mutating the input
    const updatedScores = scores.map(score => ({ ...score }));
    let cumulativePlusMinus = startPlusMinus;

    updatedScores.forEach((score) => {
      const hole = holes.find((h) => h.number === score.holeNumber);

      if (!hole || score.score === undefined) {
        score.plusMinus = undefined;
        score.plusMinusDisplay = '-';
        return;
      }

      const holePlusMinus = score.score - hole.par!;
      cumulativePlusMinus += holePlusMinus;

      score.plusMinus = cumulativePlusMinus;
      score.plusMinusDisplay = this.formatPlusMinus(cumulativePlusMinus);
    });

    return {
      updatedScores,
      finalCumulativePlusMinus: cumulativePlusMinus
    };
  }

  /**
   * Calculates plus/minus for individual hole
   * Pure function
   */
  calculateHolePlusMinus(score: number, par: number): number {
    return score - par;
  }

  /**
   * Calculates round plus/minus total
   * Pure function
   */
  calculateRoundPlusMinus(scores: ScoreResponse[], holes: Hole[]): number {
    return scores.reduce((acc, score) => {
      const hole = holes.find((h) => h.number === score.holeNumber);
      if (!hole || score.score === undefined) {
        return acc;
      }
      return acc + this.calculateHolePlusMinus(score.score, hole.par!);
    }, 0);
  }

  /**
   * Calculates round score total
   * Pure function
   */
  calculateRoundScore(scores: ScoreResponse[]): number {
    return scores.reduce((acc, score) => {
      const value = score?.score ? Number(score.score) : 0;
      return acc + (Number.isNaN(value) ? 0 : value);
    }, 0);
  }

  /**
   * Calculates scores total for a subset of holes (OUT/IN)
   * Pure function
   */
  calculateScoresTotal(scores: ScoreResponse[]): number | undefined {
    if (!scores || scores.length === 0) return undefined;

    // Check if any hole is missing a score (undefined or null, but NaN is considered a score of 0)
    const hasMissingScore = scores.some((score) => score?.score === undefined || score?.score === null);
    if (hasMissingScore) return undefined;

    return scores.reduce((acc, score) => {
      const value = Number(score.score);
      return acc + (Number.isNaN(value) ? 0 : value);
    }, 0);
  }

  /**
   * Formats plus/minus display value
   * Pure function
   */
  formatPlusMinus(plusMinus: number | undefined): string {
    if (plusMinus === undefined) return '-';
    if (plusMinus === 0) return 'E';
    if (plusMinus > 0) return `+${plusMinus}`;
    return `${plusMinus}`;
  }

  /**
   * Calculates scorecard totals and plus/minus
   * Pure function that returns calculation results without side effects
   */
  calculateScorecardTotals(
    scorecardScores: ScoreResponse[],
    holes: Hole[],
    startScore: number = 0,
    startPlusMinus: number = 0
  ): {
    roundScore: number;
    roundPlusMinus: number;
    finishScore: number;
    finishPlusMinus: number;
  } {
    const roundScore = this.calculateRoundScore(scorecardScores);
    const roundPlusMinus = this.calculateRoundPlusMinus(scorecardScores, holes);

    return {
      roundScore,
      roundPlusMinus,
      finishScore: roundScore + startScore,
      finishPlusMinus: roundPlusMinus + startPlusMinus
    };
  }

  /**
   * Validates if all scores are complete in a verify score set
   * Pure function for score completion validation
   */
  areScoresComplete(scores: any[]): boolean {
    return scores.every((sc) => sc.score !== null);
  }

  /**
   * Validates scorecard and verify scores for official marking eligibility
   * Pure function that checks multiple business rule conditions
   */
  canMarkOfficialValidation(
    scoresOUT: ScoreResponse[],
    scoresIN: ScoreResponse[],
    verifyScoresOUT: any[],
    verifyScoresIN: any[],
    validOUT: boolean,
    validIN: boolean,
    dirtyScoreCount: number
  ): boolean {
    // 1. Check for any missing System scores
    const allScoresEntered = [...scoresOUT, ...scoresIN].every(
      (score) => score.score !== undefined && score.score! > 0
    );

    if (!allScoresEntered) return false;

    // 2. Check for all complete Verify scores OR all missing Verify scores
    const allVerifyScores = [...verifyScoresOUT, ...verifyScoresIN].every(
      (score) => score.score !== null
    );

    const allMissingVerifyScores = [...verifyScoresOUT, ...verifyScoresIN].every(
      (score) => score.score === null
    );

    if (!allVerifyScores && !allMissingVerifyScores) return false;

    // 3. Check for any invalid System/Verify score discrepancies
    const isValid = validIN && validOUT;
    if (!isValid) return false;

    // 4. Check for any dirty scores
    if (dirtyScoreCount > 0) return false;

    return true;
  }

  /**
   * Validates verify scores against actual scores
   * Pure function that compares verify scores with system scores
   */
  validateVerifyScoreSet(
    scores: ScoreResponse[],
    verifyScores: any[]
  ): { isValid: boolean; updatedVerifyScores: any[] } {
    const updatedVerifyScores = verifyScores.map(verifyScore => {
      const score = scores?.find(
        (s) => s.holeNumber === verifyScore.holeNumber
      );
      
      // If verify score is null/empty, it's always valid
      if (verifyScore.score === null || verifyScore.score === undefined) {
        return { ...verifyScore, valid: true };
      }
      
      // If no matching system score found, it's considered valid (null comparison)
      if (!score || score.score === null || score.score === undefined) {
        return { ...verifyScore, valid: true };
      }
      
      // Compare actual values
      const valid = +verifyScore.score === +score.score;
      
      return {
        ...verifyScore,
        valid
      };
    });

    const isValid = updatedVerifyScores.reduce((acc, verifyScore) => {
      return acc && verifyScore.valid!;
    }, true);

    return {
      isValid,
      updatedVerifyScores
    };
  }
}
